import { inject } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { catchError, finalize, mapTo, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthService } from '@/app/core/services/auth/auth.service';
import { ToastService } from '@/app/core/services/toast/toast.service';
import { environment } from '@/environments/environment';

const HAS_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

let refreshInFlight$: Observable<void> | null = null;

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);
  const httpBackend = inject(HttpBackend);
  const bareHttp = new HttpClient(httpBackend);

  return next(req).pipe(
    catchError((err: unknown) => {
      const httpErr = err as HttpErrorResponse | undefined;
      const status = httpErr?.status ?? 0;

      const isLmsRequest = req.url.startsWith(environment.apiBaseUrl);
      const isAuthEndpoint =
        req.url.includes('/v1/auth/login') ||
        req.url.includes('/v1/auth/refresh') ||
        req.url.includes('/v1/auth/logout');

      // Don't interfere with auth endpoints; login/logout UIs handle their own errors.
      if (!isLmsRequest || isAuthEndpoint) {
        return throwError(() => err);
      }

      // Cookie auth: on 401, attempt a single refresh + retry once.
      if (status === 401) {
        // If we already retried this request, fail closed and redirect to login.
        if (req.context.get(HAS_REFRESH_RETRIED)) {
          authService.logout({ callApi: false, showToast: false });
          toastService.show({
            message: 'Session expired. Please login again.',
            type: 'error',
            duration: 3000,
            position: 'bottom-center',
          });
          return throwError(() => err);
        }

        // If the SPA doesn't consider the user logged-in, don't try refresh loops.
        if (!authService.isAuthenticated()) {
          authService.logout({ callApi: false, showToast: false });
          return throwError(() => err);
        }

        if (!refreshInFlight$) {
          refreshInFlight$ = bareHttp
            .post(`${environment.apiBaseUrl}/v1/auth/refresh`, {}, { withCredentials: true })
            .pipe(
              mapTo(void 0),
              shareReplay({ bufferSize: 1, refCount: false }),
              finalize(() => {
                refreshInFlight$ = null;
              }),
            );
        }

        return refreshInFlight$.pipe(
          switchMap(() =>
            next(
              req.clone({
                withCredentials: true,
                context: req.context.set(HAS_REFRESH_RETRIED, true),
              }),
            ),
          ),
          catchError((refreshErr: unknown) => {
            authService.logout({ callApi: false, showToast: false });
            toastService.show({
              message: 'Session expired. Please login again.',
              type: 'error',
              duration: 3000,
              position: 'bottom-center',
            });
            return throwError(() => refreshErr);
          }),
        );
      }

      // if (status === 403) {
      //   toastService.show({
      //     message: 'You do not have permission to perform this action.',
      //     type: 'error',
      //     duration: 3000,
      //     position: 'bottom-center',
      //   });
      // }

      return throwError(() => err);
    }),
  );
};
